import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import {
  FC,
  MouseEventHandler,
  PropsWithChildren,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Address, THOR, isThor } from "rujira.js";
import {
  Animated,
  Icons,
  NetworkIcon,
  Provider,
  ProviderIcon,
  useClickOutside,
  useGlobalModalContext,
  useLocale,
  usePrivateAccountRecovery,
} from "rujira.ui";
import privateImg from "rujira.ui/assets/images/ghost-wallet.svg";
import { usePreloadedBalances } from "../../common/components/Balance";
import { useGasTankBalance } from "../../redacted";
import { useRedactedV2 } from "../../redacted/v2";
import { usePrivacyProvider } from "../../redacted/provider";
import { useWalletAccounts } from "../../services/accounts";
import { useRedacted } from "../../services/redacted";
import GasTankDeposit from "./GasTankDeposit";
import { NewSpendingAccountButton, SpendingLabel } from "./NewSpendingAccount";
import { GasTankIcon } from "./PrivacyFlowModal";

const SWAP_GAS_AMOUNT = 2_000_000n;

const groupThorAccountsByAddress = (
  accounts: { address: Address; provider: Provider.Key }[]
): { address: Address; providers: Provider.Key[] }[] => {
  const byAddress = new Map<
    string,
    { address: Address; providers: Provider.Key[] }
  >();
  accounts
    .filter((x) => isThor(x.address))
    .forEach(({ address, provider }) => {
      const current = byAddress.get(address.address) ?? {
        address,
        providers: [],
      };
      if (!current.providers.includes(provider)) {
        current.providers.push(provider);
      }

      byAddress.set(address.address, current);
    });
  return Array.from(byAddress.values());
};

export const AccountSelect: FC = () => {
  const { t } = useTranslation("header");
  const [showSwitch, setShowSwitch] = useState(false);
  const switchId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { accounts, selected, select } = useWalletAccounts();
  const {
    isPrivateMode,
    publicAddress,
    setIsPrivateMode,
    stealth,
    subWalletAddress,
  } = useRedacted();
  const v2 = useRedactedV2();
  // Model B rotates one-time addresses; the account has no single address.
  const rotating = isPrivateMode && v2.modelB.enabled;
  const privacyProvider = usePrivacyProvider();
  const privateRecovery = usePrivateAccountRecovery(
    privacyProvider.v2,
    () => setShowSwitch(false),
    isPrivateMode
  );
  const ref = useClickOutside(() => setShowSwitch(false));
  const loc = useLocation();
  const history = useNavigate();
  const { toRoot } = useLocale();
  const portfolioPath = toRoot("portfolio");
  const { showModal, hideModal } = useGlobalModalContext();

  const thorAccounts = groupThorAccountsByAddress(accounts ?? []);
  const balances = usePreloadedBalances();
  const gasTankBalance = useGasTankBalance(v2.enabled ? null : publicAddress);
  const gasRuneBalance = useMemo(() => {
    try {
      return BigInt(gasTankBalance?.amount ?? "0");
    } catch {
      return 0n;
    }
  }, [gasTankBalance?.amount]);
  const estimatedSwaps = gasRuneBalance / SWAP_GAS_AMOUNT;
  const gasTankBadge = estimatedSwaps > 99n ? "99+" : estimatedSwaps.toString();
  const gasTankColor =
    estimatedSwaps === 0n
      ? "#e53935"
      : estimatedSwaps < 5n
        ? "#f57c00"
        : "#60fbd0";
  const runeWallet = useMemo(() => {
    const rune = balances.find(
      (entry) => entry.asset.metadata.symbol === "RUNE"
    );
    const account = rune?.accounts.find(
      (entry) => entry.address === publicAddress && entry.chain === THOR
    );
    return {
      balance: account?.balance ?? 0n,
      price: rune?.asset.price?.current,
    };
  }, [balances, publicAddress]);

  const showGasTankModal = () => {
    if (v2.enabled || !publicAddress || !stealth) return;
    showModal({
      title: "",
      backgroundClose: true,
      children: (
        <GasTankDeposit
          walletBalance={runeWallet.balance}
          balance={gasRuneBalance}
          runePrice={runeWallet.price}
          onCancel={hideModal}
          onSuccess={() => hideModal()}
        />
      ),
    });
  };

  return (
    <div className="flex wrap ai-c">
      <div
        ref={ref}
        className="portfolio__switcher"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setShowSwitch(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setShowSwitch(false);
            triggerRef.current?.focus();
          }
        }}>
        <button
          ref={triggerRef}
          type="button"
          id="portfolio__switcher"
          className="transparent portfolio__switcher-trigger"
          aria-label={isPrivateMode ? "Private Account" : "Select account"}
          aria-expanded={showSwitch}
          aria-controls={showSwitch ? switchId : undefined}
          onClick={() => setShowSwitch((value) => !value)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setShowSwitch(true);
            }
          }}>
          {isPrivateMode ? (
            <>
              <img
                src={privateImg}
                alt="Private account"
                className="block w-2 h-2 mr-1"
              />
              <span className="private-account-selected-label">
                {subWalletAddress && !rotating
                  ? `Private Account: ${subWalletAddress.substring(0, 6)}...${subWalletAddress.slice(-6)}`
                  : "Private Account"}
              </span>
              {rotating && <SpendingLabel />}
            </>
          ) : selected ? (
            <>
              <NetworkIcon network={THOR} className="block w-2 h-2 mr-1" />

              {
                /* width > BreakPoints.large
              ? data?.address
              :  */ selected.address.address.substring(0, 12) +
                  "..." +
                  selected.address.address.slice(-12)
              }
            </>
          ) : (
            <>
              <div
                className="block w-2 h-2 mr-1 br-2 bg-primary2 color-white"
                style={{ padding: 2 }}>
                {/* <Combine className="block" /> */}
                <Animated
                  name="combined"
                  aria-label="combined"
                  className="portfolio__switcher-all"
                />
              </div>
              Combined View (read only)
            </>
          )}
          <Icons.AngleDown className="w-2 h-2 ml-1" />
        </button>

        <AnimatePresence>
          {showSwitch && (
            <motion.nav
              id={switchId}
              aria-label="Accounts"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}>
              <>
                {v2.enabled && v2.status === "ready" && subWalletAddress && (
                  <AccountItem
                    account={{
                      address: { address: subWalletAddress, networks: [THOR] },
                      providers: [],
                    }}
                    isPrivateAccount
                    isActive={isPrivateMode}
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowSwitch(false);
                      v2.showPrivate();
                    }}>
                    {isPrivateMode && (
                      <span className="fs-10 color-grey">Active</span>
                    )}
                  </AccountItem>
                )}
                {thorAccounts.map(({ address, providers }) => (
                  <AccountItem
                    key={address.address}
                    account={{ address, providers }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSwitch(false);
                      if (v2.enabled) v2.showPublic();
                      else setIsPrivateMode(false);
                      loc.pathname !== portfolioPath && history(portfolioPath);
                      // Keep the wallet that holds this address and signs the
                      // private session; another one would lock the session.
                      const provider =
                        selected?.address.address === address.address &&
                        selected.provider &&
                        providers.includes(selected.provider)
                          ? selected.provider
                          : providers[0];
                      select({ address, provider });
                    }}
                  />
                ))}
                {!v2.enabled && stealth && subWalletAddress && (
                  <AccountItem
                    key={`${subWalletAddress}_private`}
                    account={{
                      address: {
                        address: subWalletAddress,
                        networks: [THOR],
                      },
                      providers: [selected?.provider ?? "Keplr"],
                    }}
                    isPrivateAccount={true}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSwitch(false);
                      setIsPrivateMode(true);
                      loc.pathname !== portfolioPath && history(portfolioPath);
                    }}
                  />
                )}
                {privateRecovery}
                {v2.enabled && v2.status === "ready" && (
                  <button
                    type="button"
                    className="portfolio__switcher-lock"
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowSwitch(false);
                      v2.lock();
                    }}>
                    {t("lockPrivateMode")}
                  </button>
                )}
              </>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
      {rotating && <NewSpendingAccountButton />}
      {!rotating && (
        <div
          className={clsx({
            "div bg-black w-4 h-4 flex ai-c jc-c ml-1 color-grey br-3 transition":
              true,
            "bg-hover-darkGrey hover-white": selected,
          })}>
          <Icons.Copy
            className={clsx({ "w-2": true, "opacity-10": !selected })}
            onClick={() => {
              const address = isPrivateMode
                ? subWalletAddress
                : selected?.address.address;
              if (!address) return;
              toast.success(
                isPrivateMode
                  ? "Private account address copied to clipboard"
                  : "Address copied to clipboard"
              );
              navigator.clipboard.writeText(address);
            }}
          />
        </div>
      )}
      {isPrivateMode && !v2.enabled && (
        <button
          type="button"
          id="gas-tank-modal"
          aria-label="Open swap Gas Tank"
          className="transparent div bg-black w-4 h-4 flex ai-c jc-c ml-1 br-3 transition cursor-pointer bg-hover-darkGrey relative"
          style={{ color: gasTankColor }}
          onClick={showGasTankModal}
          data-tooltip-id="global-tip"
          data-tooltip-content={`Swap Gas Tank · about ${estimatedSwaps.toString()} swaps`}>
          <GasTankIcon className="w-2 h-2 block" />
          <span
            aria-hidden="true"
            className="absolute flex ai-c jc-c condensed fw-700"
            style={{
              top: "-0.25rem",
              right: "-0.25rem",
              minWidth: "1rem",
              height: "1rem",
              padding: "0 0.2rem",
              borderRadius: "0.5rem",
              background: gasTankColor,
              color: estimatedSwaps >= 5n ? "#0d0d12" : "#ffffff",
              fontSize: "0.55rem",
              lineHeight: 1,
            }}>
            {gasTankBadge}
          </span>
        </button>
      )}
    </div>
  );
};

export const AccountItem: FC<
  PropsWithChildren<{
    account: { address: Address; providers: Provider.Key[] };
    onClick: MouseEventHandler<HTMLButtonElement>;
    isPrivateAccount?: boolean;
    isActive?: boolean;
  }>
> = ({
  account,
  onClick,
  isPrivateAccount = false,
  isActive = false,
  children,
}) => {
  const { isPrivateMode } = useRedacted();
  const v2 = useRedactedV2();
  // Model B: a rotating one-time address is never shown as the account.
  const rotating = isPrivateMode && v2.modelB.enabled;
  return (
    <button onClick={onClick} aria-current={isActive ? true : undefined}>
      <div className="flex ai-c w-full">
        <div className="flex">
          {isPrivateAccount ? (
            <img
              src={privateImg}
              alt="Private account"
              className="block w-3.5 h-3.5 mr-1"
            />
          ) : (
            <NetworkIcon network={THOR} className="block w-3.5 h-3.5 mr-1" />
          )}
        </div>
        <div className="mr-2 flex dir-c ai-s">
          <span className="fs-12 color-white">
            {isPrivateAccount && rotating
              ? "Private Account"
              : isPrivateAccount
                ? `Private Account: ${account.address.address.substring(0, 6)}...${account.address.address.slice(-6)}`
                : account.address.address.substring(0, 12) +
                  "..." +
                  account.address.address.slice(-12)}
          </span>
          {account.providers.length > 0 && (
            <div className="fs-10 mt-0.5 flex ai-c">
              {account.providers.map((p, index) => (
                <div className="flex dir-r" key={p}>
                  <ProviderIcon
                    provider={p}
                    className="block w-1.5 h-a mr-0.5"
                    selected={true}
                  />
                  <span className={"mr-1"}>{account.providers[index]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {children}
      </div>
    </button>
  );
};
